'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiRequest } from './apiClient';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: 'ADMIN' | 'TECHNICIAN';
  phone?: string | null;
  avatarUrl?: string | null;
  timezone?: string;
  dateFormat?: string;
  currency?: string;
  createdAt?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  logout: () => Promise<void>;
  setUser: (user: AuthUser) => void;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: () => {},
  logout: async () => {},
  setUser: () => {},
  refetch: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      const res = await apiRequest('/api/auth/me');
      const json = await res.json();
      if (json.success) {
        setUserState(json.data);
        localStorage.setItem('klugcmms-user', JSON.stringify(json.data));
      } else {
        setUserState(null);
      }
    } catch {
      setUserState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('klugcmms-access-token');
    if (!token) { setLoading(false); return; }
    refetch();
  }, [refetch]);

  const login = (user: AuthUser, accessToken: string, refreshToken: string) => {
    localStorage.setItem('klugcmms-user', JSON.stringify(user));
    localStorage.setItem('klugcmms-access-token', accessToken);
    localStorage.setItem('klugcmms-refresh-token', refreshToken);
    setUserState(user);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('klugcmms-refresh-token');
    try {
      await apiRequest('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } finally {
      localStorage.removeItem('klugcmms-user');
      localStorage.removeItem('klugcmms-access-token');
      localStorage.removeItem('klugcmms-refresh-token');
      setUserState(null);
    window.location.href = `${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/sign-up-login`;
    }
  };

  const setUser = (user: AuthUser) => {
    setUserState(user);
    localStorage.setItem('klugcmms-user', JSON.stringify(user));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, refetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
}
